import { format } from 'date-fns';
import { FundHistoricPriceIntervalType } from '@pm-netbank/constants';
import { buildEndpoint } from '../helpers/BuildEndpoint';
import BasePaths from './BasePaths';

const fundsBaseUrl = BasePaths.Funds;

const fundsPathDefinitions = {
  equityAccountHoldings: '/equityAccountHoldings',
  fundAccountsWithHoldings: '/accounts-with-holdings',
  fundDetails: '/list/{isin}/details',
  fundHistoricPricesWithIntervalType:
    '/list/{isin}/historic-prices?timeIntervalType={timeIntervalType}',
  fundHoldingsOverview: '/holdings',
  fundHoldingsPerformanceWithDuration:
    '/holdings/performance?type={type}&duration={duration}',
  fundTransactions: `/transactions`,
  fundTransactionDetails: `/transactions/{transactionId}/details`,
  fundOrders: `/orders`,
  fundOrderDelete: `/orders/{orderType}/{orderId}`,
  fundOrderDetails: `/orders/{orderId}/details`,
  funds: '/list',
  savingsAgreement: '/savings-agreements/{savingsAgreementId}',
  savingsAgreements: '/savings-agreements',
  savingsAgreementDetails: '/savings-agreements/{savingsAgreementId}/details',
  savingsAgreementEditHelper:
    '/savings-agreements/{savingsAgreementId}/edit-helper',
  selvvalgtEligibleFunds: '/list?selvvalgtEligible=true',
};

const fundsEndpoints = {
  equityAccountHoldings: () =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.equityAccountHoldings),

  fundAccountsWithHoldings: () =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundAccountsWithHoldings),

  fundDetails: (isin: string) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundDetails, { isin }),

  fundHoldingsOverview: () =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundHoldingsOverview),

  fundHoldingsPerformanceWithDuration: (
    type: string,
    duration: number | null
  ) =>
    buildEndpoint(
      fundsBaseUrl,
      fundsPathDefinitions.fundHoldingsPerformanceWithDuration,
      { type, duration: duration?.toString() || null }
    ),

  fundTransactions: (fromDate?: Date, toDate?: Date) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundTransactions, {
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : null,
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : null,
    }),

  fundTransactionDetails: (transactionId: string) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundTransactionDetails, {
      transactionId,
    }),

  fundOrders: () =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundOrders),

  fundOrderDetails: (orderId: string) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundOrderDetails, {
      orderId,
    }),

  fundOrderDelete: (orderType: string, orderId: string) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.fundOrderDelete, {
      orderType,
      orderId,
    }),

  savingsAgreements: () =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.savingsAgreements),

  savingsAgreement: (savingsAgreementId: string) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.savingsAgreement, {
      savingsAgreementId,
    }),

  savingsAgreementDetails: (savingsAgreementId: string) =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.savingsAgreementDetails, {
      savingsAgreementId,
    }),

  savingsAgreementEditHelper: (savingsAgreementId: string) =>
    buildEndpoint(
      fundsBaseUrl,
      fundsPathDefinitions.savingsAgreementEditHelper,
      {
        savingsAgreementId,
      }
    ),

  selvvalgtEligibleFunds: () =>
    buildEndpoint(fundsBaseUrl, fundsPathDefinitions.selvvalgtEligibleFunds),

  funds: () => buildEndpoint(fundsBaseUrl, fundsPathDefinitions.funds),

  fundHistoricPricesWithIntervalType: (
    isin: string,
    timeIntervalType: FundHistoricPriceIntervalType
  ) =>
    buildEndpoint(
      fundsBaseUrl,
      fundsPathDefinitions.fundHistoricPricesWithIntervalType,
      { isin, timeIntervalType }
    ),
};

export { fundsEndpoints, fundsPathDefinitions };
